import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../modals/modal.component';
import { MindrayCredential } from '../../dish.types';
import { toast } from 'react-toastify';
import './MindrayCredentialsModal.scss';

interface MindrayCredentialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  credentials: MindrayCredential[];
}

/**
 * MindrayCredentialsModal Component
 * @description Modal to display Mindray credentials before opening the Mindray system
 * @param {MindrayCredentialsModalProps} props
 * @returns {JSX.Element}
 */
export function MindrayCredentialsModal({ isOpen, onClose, credentials }: MindrayCredentialsModalProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedCredentialIndex, setSelectedCredentialIndex] = useState<number | null>(null);
  const [copiedField, setCopiedField] = useState<'user' | 'password' | null>(null);

  const isTesting = false; // Set to true to use test credentials

  const testCredentials: MindrayCredential[] = [
    {
      uuid: '123e4567-e89b-12d3-a456-426614174000',
      n1_name_identifier: 'ICU Monitor 1',
      mindray_user: 'admin',
      mindray_password: 'password123'
    },
    {
      uuid: '223e4567-e89b-12d3-a456-426614174001',
      n1_name_identifier: 'ICU Monitor 2',
      mindray_user: 'user',
      mindray_password: 'pass456'
    }
  ];

  const handleClose = () => {
    setSelectedCredentialIndex(null);
    setCopiedField(null);
    onClose();
  };

  const handleBack = () => {
    setSelectedCredentialIndex(null);
    setCopiedField(null);
  };

  const handleOpenMindray = () => {
    window.open('https://mindray.comitas.es', '_blank');
  };

  const handleCopy = async (text: string, field: 'user' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(t('modal.mindray.copied'), { autoClose: 1500 });
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error(t('modal.mindray.copyError'));
    }
  };

  // Choose data source: testCredentials when testing, otherwise real credentials
  const dataCredentials = isTesting ? testCredentials : credentials;

  const isLoading = dataCredentials.length === 0;
  const hasMultipleCredentials = dataCredentials.length > 1;
  const showList = hasMultipleCredentials && selectedCredentialIndex === null;
  const showDetails = dataCredentials.length > 0 && (dataCredentials.length === 1 || selectedCredentialIndex !== null);
  const currentCredential = dataCredentials[selectedCredentialIndex ?? 0];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      type='warning'
    >
      <div className="mindray-modal">
        <div className="mindray-modal__header">
          <i className="las la-heartbeat mindray-modal__icon" />
          <h2>{t('modal.mindray.title')}</h2>
          <p className="mindray-modal__subtitle">{t('modal.mindray.subtitle')}</p>
        </div>

        <div className="mindray-modal__content">
          {/* Loading state */}
          {isLoading && (
            <div className="mindray-modal__loading">
              <i className="las la-spinner la-spin" />
              <p>{t('modal.mindray.loading')}</p>
            </div>
          )}

          {/* List view when multiple credentials and none selected */}
          {showList && (
            <div className="mindray-modal__list">
              <p className="mindray-modal__list-title">
                <i className="las la-list" />
                {t('modal.mindray.select')}
              </p>
              <div className="mindray-modal__devices">
                  {dataCredentials.map((cred, index) => (
                  <button
                    key={cred.uuid}
                    className="mindray-modal__device-btn"
                    onClick={() => setSelectedCredentialIndex(index)}
                  >
                    <i className="las la-desktop" />
                    <span>{cred.n1_name_identifier || `N1 ${index + 1}`}</span>
                    <i className="las la-angle-right" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Detail view when single credential or credential selected */}
          {showDetails && currentCredential && (
            <div className="mindray-modal__details">
              {hasMultipleCredentials && (
                <div className="mindray-modal__device-name">
                  <i className="las la-desktop" />
                  <span>{currentCredential.n1_name_identifier}</span>
                </div>
              )}

              <p className="mindray-modal__instruction">
                <i className="las la-info-circle" />
                {t('modal.mindray.content')}
              </p>

              <div className="mindray-modal__credentials">
                <div className="mindray-modal__credential-row">
                  <div className="mindray-modal__credential-info">
                    <label>{t('modal.mindray.username')}</label>
                    <span className="mindray-modal__credential-value">
                      {currentCredential.mindray_user}
                    </span>
                  </div>
                  <button
                    className={`mindray-modal__copy-btn ${copiedField === 'user' ? 'copied' : ''}`}
                    onClick={() => handleCopy(currentCredential.mindray_user, 'user')}
                    title={t('modal.mindray.copyTooltip') as string}
                  >
                    <i className={copiedField === 'user' ? 'las la-check' : 'las la-copy'} />
                  </button>
                </div>

                <div className="mindray-modal__credential-row">
                  <div className="mindray-modal__credential-info">
                    <label>{t('modal.mindray.password')}</label>
                    <span className="mindray-modal__credential-value">
                      {currentCredential.mindray_password}
                    </span>
                  </div>
                  <button
                    className={`mindray-modal__copy-btn ${copiedField === 'password' ? 'copied' : ''}`}
                    onClick={() => handleCopy(currentCredential.mindray_password, 'password')}
                    title={t('modal.mindray.copyTooltip') as string}
                  >
                    <i className={copiedField === 'password' ? 'las la-check' : 'las la-copy'} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mindray-modal__actions">
          {hasMultipleCredentials && selectedCredentialIndex !== null && (
            <button className="mindray-modal__btn mindray-modal__btn--secondary" onClick={handleBack}>
              <i className="las la-arrow-left" />
              {t('modal.mindray.back')}
            </button>
          )}

          <button className="mindray-modal__btn mindray-modal__btn--cancel" onClick={handleClose}>
            {t('modal.mindray.close')}
          </button>

          {showDetails && (
            <button className="mindray-modal__btn mindray-modal__btn--primary" onClick={handleOpenMindray}>
              <i className="las la-external-link-alt" />
              {t('modal.mindray.open')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
